import * as React from 'react';
import Collapse from 'antd/lib/collapse';
import Text from 'antd/lib/typography/Text';
import Paragraph from 'antd/lib/typography/Paragraph';
import {
    ApiOutlined,
    BulbOutlined,
    CodeOutlined,
    CommentOutlined,
    DisconnectOutlined,
    DislikeOutlined,
    DownOutlined,
    FlagOutlined,
    LikeOutlined,
    ReadFilled,
    RightOutlined,
    RiseOutlined,
} from '@ant-design/icons';
import { Citation, PotentialCause, PotentialCausesSummary } from '@/types';
import { antGrey, drawerSectionSeparator, rwColors } from '@/components/styles/globalStyles';

const CAUSE_ICON: Record<PotentialCause, React.ReactNode> = {
    [PotentialCause.Code]: <CodeOutlined />,
    [PotentialCause.FeatureFlag]: <FlagOutlined />,
    [PotentialCause.TrafficSpike]: <RiseOutlined />,
    [PotentialCause.Dependency]: <ApiOutlined />,
    [PotentialCause.IntegrationIssue]: <DisconnectOutlined />,
};

const FEEDBACK_ICON_STYLE: React.CSSProperties = {
    color: antGrey.grey6,
    fontSize: 18,
    cursor: 'pointer',
};

const ROW_STYLE: React.CSSProperties = {
    margin: '0 16px',
    padding: '12px 0',
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
};

const CitationLink: React.FC<{ citation: Citation; text?: string }> = ({ citation, text }) =>
    citation.url ?
        <a
            href={citation.url}
            target="_blank"
            rel="noreferrer"
            style={{ color: antGrey.grey9, textDecoration: 'underline' }}
        >
            {text ?? citation.label}
        </a>
    :   <>{citation.label}</>;

type EvidenceGroup = { heading?: string; items: string[] };

const HowThisWasIdentified: React.FC<{ groups: EvidenceGroup[] }> = ({ groups }) => (
    <Collapse
        className="triage-collapse-evidence"
        ghost
        expandIcon={({ isActive }) =>
            isActive ?
                <DownOutlined style={{ color: antGrey.grey6, fontSize: 12 }} />
            :   <RightOutlined style={{ color: antGrey.grey6, fontSize: 12 }} />
        }
        items={[
            {
                key: 'evidence',
                label: (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <ReadFilled style={{ color: rwColors.statusYellow, fontSize: 16 }} />
                        <Text
                            style={{
                                color: antGrey.grey7,
                                fontSize: 12,
                                letterSpacing: 1.2,
                                textTransform: 'uppercase',
                            }}
                        >
                            How this was identified
                        </Text>
                    </div>
                ),
                children: groups.map((group, groupIndex) => (
                    <React.Fragment key={group.heading ?? groupIndex}>
                        {group.heading && (
                            <Text style={{ display: 'block', paddingLeft: 24, color: antGrey.grey9, fontWeight: 600 }}>
                                {group.heading}
                            </Text>
                        )}
                        <ul style={{ margin: 0, paddingLeft: 40, color: antGrey.grey9, fontSize: 14 }}>
                            {group.items.map((item, index) => (
                                <li key={index}>{item}</li>
                            ))}
                        </ul>
                    </React.Fragment>
                )),
            },
        ]}
    />
);

export const PotentialCausesSection: React.FC<{ potentialCauses?: PotentialCausesSummary }> = ({ potentialCauses }) => {
    if (!potentialCauses?.causes?.length) {
        return null;
    }
    const citations = potentialCauses.causes.reduce<Citation[]>((acc, cause) => acc.concat(cause.citations ?? []), []);
    const evidence = citations.reduce<string[]>((acc, citation) => acc.concat(citation.evidence ?? []), []);
    // With several causes, each citation's bullets sit under its label so they don't read as one argument.
    const evidenceGroups: EvidenceGroup[] =
        potentialCauses.causes.length > 1 ?
            citations
                .filter((citation) => citation.evidence?.length)
                .map((citation) => ({ heading: citation.label, items: citation.evidence ?? [] }))
        :   [{ items: evidence }];
    // A code, feature flag, integration issue or dependency cause alone is summarized by the code, not AI; older issues carry no evidence and keep the AI text alone.
    const summarizedWithoutAI =
        potentialCauses.causes.length === 1 &&
        [
            PotentialCause.Code,
            PotentialCause.FeatureFlag,
            PotentialCause.IntegrationIssue,
            PotentialCause.Dependency,
        ].includes(potentialCauses.causes[0].cause) &&
        evidence.length > 0;
    return (
        <Collapse
            className="triage-collapse triage-collapse-causes"
            ghost
            bordered={false}
            defaultActiveKey={['section']}
            items={[
                {
                    key: 'section',
                    showArrow: false,
                    style: { borderBottom: drawerSectionSeparator },
                    label: (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                            <BulbOutlined style={{ color: rwColors.statusYellow }} />
                            <Text style={{ color: rwColors.statusYellow, fontSize: 14, fontWeight: 600 }}>
                                Potential cause(s) identified
                            </Text>
                        </div>
                    ),
                    children: (
                        <>
                            <div style={ROW_STYLE}>
                                {potentialCauses.aiTitle && (
                                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                                        <span style={{ color: antGrey.grey6, fontSize: 22, flexShrink: 0 }}>
                                            {CAUSE_ICON[potentialCauses.causes[0]?.cause]}
                                        </span>
                                        <Text style={{ fontSize: 20, color: antGrey.grey9 }}>
                                            {potentialCauses.aiTitle}
                                        </Text>
                                    </div>
                                )}
                                {(potentialCauses.aiDescription || citations.length > 0) && (
                                    <Paragraph style={{ color: antGrey.grey8, fontSize: 14, marginBottom: 0 }}>
                                        {potentialCauses.aiDescription}
                                        {citations.map((citation, index) => (
                                            <React.Fragment key={`${citation.referenceId ?? citation.label}-${index}`}>
                                                {(potentialCauses.aiDescription || index > 0) && <br />}
                                                <CitationLink
                                                    citation={citation}
                                                    text={summarizedWithoutAI ? 'link to source' : undefined}
                                                />
                                            </React.Fragment>
                                        ))}
                                    </Paragraph>
                                )}
                            </div>
                            {evidence.length > 0 && (
                                <div style={{ ...ROW_STYLE, padding: '8px 0 28px' }}>
                                    <HowThisWasIdentified groups={evidenceGroups} />
                                </div>
                            )}
                            <div
                                style={{
                                    ...ROW_STYLE,
                                    paddingTop: 0,
                                    flexDirection: 'row',
                                    alignItems: 'center',
                                    gap: 16,
                                }}
                            >
                                {/* No onClick: feedback interactions are attached to these icons in Userflow */}
                                <LikeOutlined style={FEEDBACK_ICON_STYLE} />
                                <DislikeOutlined style={FEEDBACK_ICON_STYLE} />
                                <CommentOutlined style={FEEDBACK_ICON_STYLE} />
                            </div>
                        </>
                    ),
                },
            ]}
        />
    );
};
